import {
  format,
  isSameYear,
  isThisYear,
  isToday,
  isYesterday,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { vi } from 'date-fns/locale';

// ---------------------------------------------------------------------------
// BonBoZ: date-fns mặc định luôn trả tiếng Anh ("less than a minute ago"),
// và Chatwoot chưa hề truyền locale vào — nên mọi mốc thời gian tương đối
// trong giao diện đều là tiếng Anh dù đã chọn Tiếng Việt.
//
// Sửa ở ĐÂY chứ không vá từng component: chỉ riêng `dynamicTime` đã được dùng
// ở danh sách hội thoại, ghi chú khách, phiên đăng nhập... Vá từng chỗ là chắc
// chắn sót.
//
// Ngôn ngữ đọc từ thẻ <html lang> do App.vue đặt khi đổi locale — helper này
// nằm ở `shared/` (dùng chung cả widget lẫn dashboard) nên không import được
// i18n của dashboard vào, sẽ thành phụ thuộc vòng.
// ---------------------------------------------------------------------------
const BANG_LOCALE = { vi };

const locale = () => {
  const ma = (
    typeof document === 'undefined' ? '' : document.documentElement.lang || ''
  ).split('-')[0];
  return BANG_LOCALE[ma];
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat = 'MMM d, yyyy') => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const messageDate = format(messageTime, dateFormat);
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, 'LLL d y, h:mm a');
  }
  return messageDate;
};

/**
 * Formats a Unix timestamp relative to today: the time for today, a caller-
 * supplied label for yesterday, and a date otherwise. The yesterday label is
 * passed in so the caller keeps ownership of translation.
 * @param {number} time - Unix timestamp.
 * @param {string} yesterdayLabel - Localized label shown for yesterday.
 * @returns {string} Formatted timestamp string.
 */
export const relativeDayTimestamp = (time, yesterdayLabel) => {
  const date = fromUnixTime(time);
  if (isToday(date)) return format(date, 'h:mm a');
  if (isYesterday(date)) return yesterdayLabel;
  if (isThisYear(date)) return format(date, 'MMM d');
  return format(date, 'MMM d, yyyy');
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = time => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, { addSuffix: true, locale: locale() });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
// ---------------------------------------------------------------------------
// BonBoZ: mốc thời gian NGẮN, tính thẳng từ timestamp.
//
// VÌ SAO PHẢI VIẾT LẠI: `shortTimestamp` bên dưới nhận vào một CHUỖI đã dịch
// rồi cắt chuỗi đó bằng `.replace(' days ago', 'd')` — nghĩa là nó chỉ chạy
// được với tiếng Anh. Mọi chỗ gọi đều theo kiểu `shortTimestamp(dynamicTime(t))`:
// định dạng ra chuỗi rồi tìm cách tháo ngược chuỗi đó.
//
// Từ lúc `dynamicTime` trả tiếng Việt, không mẫu `.replace` nào khớp nữa, nên
// cả câu dài đi thẳng ra thẻ hội thoại — đó là lý do một thẻ hiện
// "8 ngày trước • khoảng 17 giờ trước" thay vì "8 ngày • 17 giờ".
//
// Cách sửa: KHÔNG cắt chuỗi nữa. Tính bằng số từ chính timestamp rồi mới ghép
// chữ theo ngôn ngữ — đúng với mọi ngôn ngữ, kể cả ngôn ngữ thêm sau này.
// ---------------------------------------------------------------------------
const DON_VI = [
  { giay: 31536000, vi: 'năm', en: 'y' },
  { giay: 2592000, vi: 'tháng', en: 'mo' },
  { giay: 86400, vi: 'ngày', en: 'd' },
  { giay: 3600, vi: 'giờ', en: 'h' },
  { giay: 60, vi: 'phút', en: 'm' },
];

/**
 * Mốc thời gian tương đối, dạng ngắn, tính thẳng từ timestamp.
 * @param {number} timestamp - Unix timestamp (giây).
 * @param {boolean} [themTruoc=false] - thêm "trước" / "ago" ở cuối.
 * @returns {string} ví dụ "8 ngày", "17 giờ", "vừa xong" — hoặc "8d", "17h", "now".
 */
export const mocNgan = (timestamp, themTruoc = false) => {
  if (!timestamp) return '';
  const laViet = document?.documentElement?.lang?.startsWith('vi');
  // Khoảng cách TUYỆT ĐỐI, để dùng được cả mốc tương lai ("tạm hoãn đến…")
  // lẫn mốc quá khứ. Bên gọi tự quyết câu chữ trước/sau.
  const giay = Math.abs(Math.floor(Date.now() / 1000 - timestamp));

  // Dưới một phút: nói "vừa xong" chứ không "0 phút".
  if (giay < 60) return laViet ? 'vừa xong' : 'now';

  const dv = DON_VI.find(d => giay >= d.giay);
  const so = Math.floor(giay / dv.giay);
  // Tiếng Việt cần khoảng trắng ("8 ngày"), tiếng Anh thì không ("8d").
  const goc = laViet ? `${so} ${dv.vi}` : `${so}${dv.en}`;
  if (!themTruoc) return goc;
  return laViet ? `${goc} trước` : `${goc} ago`;
};

/**
 * Mốc thời gian cho danh sách hội thoại và ghi chú khách.
 *
 * Hôm nay chỉ cần giờ phút, hôm qua thì thêm chữ "hôm qua", xa hơn nữa thì
 * ngày tháng — người trực không cần biết tin thứ Bảy tuần trước đến lúc mấy giờ,
 * mà "8 ngày trước" thì lại phải tự nhẩm ra ngày.
 *
 * @param {number} timestamp - Unix timestamp (giây).
 * @returns {string} ví dụ "14:22", "09:07 hôm qua", "30/8", "12/11/2025".
 */
export const mocGioNgay = timestamp => {
  if (!timestamp) return '';
  const date = fromUnixTime(timestamp);
  const laViet = document?.documentElement?.lang?.startsWith('vi');
  if (isToday(date)) return format(date, 'HH:mm');
  if (isYesterday(date)) {
    return `${format(date, 'HH:mm')} ${laViet ? 'hôm qua' : 'yesterday'}`;
  }
  return format(date, isThisYear(date) ? 'd/M' : 'd/M/yyyy');
};

export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
